import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { goalsAPI } from '../../data/goals';
import { unwrap } from '../../data/api';
import { invalidateFinancialData } from '../financialCache';
import { invalidateGuidedFlow } from '../guidedFlow/queries';

export const goalsKeys = {
  all: ['goals'],
  list: (params) => [...goalsKeys.all, 'list', params || {}],
  detail: (id) => [...goalsKeys.all, 'detail', id],
  progress: (id) => [...goalsKeys.all, 'progress', id],
  priority: () => [...goalsKeys.all, 'priority'],
  milestones: (params) => [...goalsKeys.all, 'milestones', params || {}],
};


export function useGoalsQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: goalsKeys.list(params),
    queryFn: () => goalsAPI.list(params).then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useGoalQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: goalsKeys.detail(id),
    queryFn: () => goalsAPI.retrieve(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function useGoalProgressQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: goalsKeys.progress(id),
    queryFn: () => goalsAPI.getProgress(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function usePriorityGoalQuery(queryOptions = {}) {
  return useQuery({
    queryKey: goalsKeys.priority(),
    queryFn: () => goalsAPI.suggestPriority().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useGoalMilestonesQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: goalsKeys.milestones(params),
    queryFn: () => goalsAPI.listMilestones(params).then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useCreateGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => goalsAPI.create(payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalsKeys.all, exact: false });
      qc.invalidateQueries({ queryKey: ['pots'], exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useUpdateGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      goalsAPI.update(id, payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: goalsKeys.all, exact: false });
    },
  });
}

export function useDeleteGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => goalsAPI.remove(id).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateFinancialData(qc);
    },
  });
}

export function useCompleteGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => goalsAPI.complete(id).then((r) => unwrap(r)),
    onSuccess: (_data, id) => {
      invalidateGoal(qc, id);
    },
  });
}

export function useCancelGoalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => goalsAPI.cancel(id).then((r) => unwrap(r)),
    onSuccess: (_data, id) => {
      invalidateGoal(qc, id);
    },
  });
}

export function useUploadGoalImageMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) =>
      goalsAPI.uploadImage(id, file).then((r) => unwrap(r)),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: goalsKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: goalsKeys.all, exact: false });
    },
  });
}

function invalidateGoal(qc, goalId) {
  qc.invalidateQueries({ queryKey: goalsKeys.detail(goalId) });
  qc.invalidateQueries({ queryKey: goalsKeys.progress(goalId) });
  qc.invalidateQueries({ queryKey: goalsKeys.all, exact: false });
}

export function useAddChecklistItemMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, title }) =>
      goalsAPI.addChecklistItem(goalId, title).then((r) => unwrap(r)),
    onSuccess: (_data, vars) => invalidateGoal(qc, vars.goalId),
  });
}

export function useToggleChecklistItemMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, itemId }) =>
      goalsAPI.toggleChecklistItem(goalId, itemId).then((r) => unwrap(r)),
    onSuccess: (_data, vars) => invalidateGoal(qc, vars.goalId),
  });
}

export function useDeleteChecklistItemMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, itemId }) =>
      goalsAPI.deleteChecklistItem(goalId, itemId).then((r) => unwrap(r)),
    onSuccess: (_data, vars) => invalidateGoal(qc, vars.goalId),
  });
}
